import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resellers, type ResellerRow } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, UserPlus, ToggleLeft } from 'lucide-react';
import { TimezonePicker } from '@/components/TimezonePicker';
import { PhoneInput, CountryPrefixSelect } from '@/components/PhoneInput';
import { resellerEmailSettings } from '@/api/resellerEmailSettings';
import { BackupConfigCard } from '@/components/BackupConfigCard';
import { formatRef } from '@/lib/formatRef';

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  defaultLanguage: z.enum(['en', 'he', 'yi']),
  // '+' + 1-3 digits, or null = inherit the platform default (migration 0113).
  defaultCountryCode: z.string().regex(/^\+[1-9]\d{0,2}$/, 'Invalid country code').nullable(),
});
type EditValues = z.infer<typeof editSchema>;

const LANGUAGES: Array<{ value: 'en' | 'he' | 'yi'; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'he', label: 'Hebrew' },
  { value: 'yi', label: 'Yiddish' },
];

export function ResellerDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // `id` existence guard is below; mutationFn closures rely on UI-level guarding.
  const { data, isLoading, error } = useQuery({
    queryKey: ['reseller', id],
    queryFn: () => resellers.get(id!),
    enabled: Boolean(id),
  });
  const toggle = useMutation({
    mutationFn: () => resellers.update(id!, { active: !data!.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reseller', id] }),
  });
  const del = useMutation({
    mutationFn: () => resellers.remove(id!),
    onSuccess: () => navigate('/resellers'),
  });
  const update = useMutation({
    mutationFn: (v: EditValues) =>
      resellers.update(id!, {
        name: v.name,
        timezone: v.timezone,
        defaultLanguage: v.defaultLanguage,
        defaultCountryCode: v.defaultCountryCode,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller', id] });
      qc.invalidateQueries({ queryKey: ['resellers'] });
      // Phone fields cache the resolved default prefix — refresh it so a
      // changed default country code shows up without a reload.
      qc.invalidateQueries({ queryKey: ['phoneDefaults'] });
      setEditOpen(false);
    },
  });

  if (!id) return <div className="text-sm text-destructive">{t('common.invalidUrl')}</div>;
  if (isLoading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error || !data) return <div className="text-sm text-destructive">{error instanceof Error ? error.message : t('common.notFound')}</div>;

  function inviteUserHref() {
    return `/users/new?resellerId=${id}`;
  }

  return (
    <>
      <PageHeader
        eyebrow={`${t('nav.resellers')} · #${formatRef(data.reseller_number)}`}
        title={data.name}
        description={t('reseller.customerIdsStart', { start: data.reseller_number * 1000 })}
        actions={
          <>
            <Button variant="outline" onClick={() => { setEditError(null); setEditOpen(true); }}>
              <Pencil className="h-4 w-4" /> {t('common.edit')}
            </Button>
            <Button asChild variant="outline">
              <Link to={`/resellers/${id}/settings/features`}>
                <ToggleLeft className="h-4 w-4" /> {t('reseller.featuresAndPricing')}
              </Link>
            </Button>
            <Button asChild>
              <Link to={inviteUserHref()} state={{ resellerId: id, resellerName: data.name }}>
                <UserPlus className="h-4 w-4" /> {t('reseller.inviteUser')}
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('common.details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="text-muted-foreground">{t('reseller.resellerNumber')}</dt>
              <dd className="text-right sm:text-left font-mono tabular-nums">{formatRef(data.reseller_number)}</dd>

              <dt className="text-muted-foreground">{t('common.name')}</dt>
              <dd className="text-right sm:text-left">{data.name}</dd>

              <dt className="text-muted-foreground">{t('reseller.slug')}</dt>
              <dd className="text-right sm:text-left"><code className="text-xs">{data.slug}</code></dd>

              <dt className="text-muted-foreground">{t('common.timezone')}</dt>
              <dd className="text-right sm:text-left">{data.timezone}</dd>

              <dt className="text-muted-foreground">Default country code</dt>
              <dd className="text-right sm:text-left">
                {data.default_country_code
                  ? <span className="font-mono" dir="ltr">{data.default_country_code}</span>
                  : <span className="text-muted-foreground">Platform default</span>}
              </dd>

              <dt className="text-muted-foreground">{t('common.language')}</dt>
              <dd className="text-right sm:text-left">{data.default_language.toUpperCase()}</dd>

              <dt className="text-muted-foreground">{t('common.status')}</dt>
              <dd className="text-right sm:text-left">
                <Badge variant={data.active ? 'default' : 'secondary'}>
                  {data.active ? t('common.active') : t('common.inactive')}
                </Badge>
              </dd>

              <dt className="text-muted-foreground">{t('common.createdAt')}</dt>
              <dd className="text-right sm:text-left">{new Date(data.created_at).toLocaleString()}</dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('common.actions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm">
            <div className="space-y-2">
              <p className="font-medium">{t('customer.activation')}</p>
              <Button
                variant="outline"
                size="sm"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate()}
              >
                {toggle.isPending
                  ? (data.active ? t('customer.deactivating') : t('customer.activating'))
                  : (data.active ? t('customer.deactivate') : t('customer.activate'))}
              </Button>
            </div>

            <PlatformEmailToggle resellerId={data.id} />

            <div className="space-y-2">
              <p className="font-medium">{t('reseller.deleteReseller')}</p>
              <p className="text-xs text-muted-foreground">
                {t('reseller.deleteResellerDescription')}
              </p>
              <Button
                variant="destructive"
                size="sm"
                disabled={del.isPending}
                onClick={() => {
                  if (confirm(t('reseller.deleteConfirmMessage', { name: data.name }))) del.mutate();
                }}
              >
                {del.isPending ? t('common.deleting') : t('reseller.deleteReseller')}
              </Button>
              {del.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {del.error instanceof Error ? del.error.message : t('common.failedToDelete')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <BackupConfigCard scope="reseller" tenantId={data.id} />

        <RecordingDialInCard
          resellerId={data.id}
          current={data.recording_dial_in_e164 ?? null}
        />

        <RecycleBinConfigCard reseller={data} />
      </div>

      <EditResellerDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        defaults={{
          name: data.name,
          timezone: data.timezone,
          defaultLanguage: data.default_language,
          defaultCountryCode: data.default_country_code ?? null,
        }}
        isPending={update.isPending}
        serverError={editError}
        onSubmit={async (v) => {
          setEditError(null);
          try {
            await update.mutateAsync(v);
          } catch (err) {
            setEditError(err instanceof Error ? err.message : t('common.failedToSave'));
          }
        }}
      />
    </>
  );
}

function EditResellerDialog({
  open, onOpenChange, defaults, onSubmit, isPending, serverError,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaults: EditValues;
  onSubmit: (v: EditValues) => void | Promise<void>;
  isPending: boolean;
  serverError: string | null;
}) {
  const { t } = useTranslation();
  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: defaults,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) reset(defaults);
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('reseller.editTitle')}</DialogTitle>
          <DialogDescription>
            {t('reseller.editDescription')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="space-y-1">
            <Label htmlFor="edit-name">{t('common.name')}</Label>
            <Input
              id="edit-name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'edit-name-error' : undefined}
              {...register('name')}
            />
            {errors.name && (
              <p id="edit-name-error" role="alert" className="text-xs text-destructive">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-timezone">{t('common.timezone')}</Label>
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <TimezonePicker value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
            {errors.timezone && (
              <p id="edit-timezone-error" role="alert" className="text-xs text-destructive">
                {errors.timezone.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-defaultCountryCode">Default country code</Label>
            <Controller
              name="defaultCountryCode"
              control={control}
              render={({ field }) => (
                <CountryPrefixSelect
                  id="edit-defaultCountryCode"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  inheritLabel="Inherit platform default"
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              Phone fields for this reseller&rsquo;s accounts pre-fill this prefix.
              Customers can override it on their own page.
            </p>
            {errors.defaultCountryCode && (
              <p role="alert" className="text-xs text-destructive">
                {errors.defaultCountryCode.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-defaultLanguage">{t('reseller.defaultLanguage')}</Label>
            <select
              id="edit-defaultLanguage"
              className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              {...register('defaultLanguage')}
            >
              {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>

          {serverError && (
            <p role="alert" className="text-sm text-destructive">{serverError}</p>
          )}

          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────
// Recording dial-in card — set/clear resellers.recording_dial_in_e164
// ──────────────────────────────────────────────────────────

const E164_RE = /^\+[1-9]\d{1,14}$/;

/**
 * Lets super_admin set (or clear) the E.164 number that triggers the admin
 * recording dial-in flow for this reseller. When a caller dials this number,
 * the platform authenticates them by caller ID (verified_numbers), then guides
 * them through folder selection + recording — no portal access required.
 */
export function RecordingDialInCard({
  resellerId,
  current,
}: {
  resellerId: string;
  current: string | null;
}) {
  const qc = useQueryClient();

  const [inputValue, setInputValue] = useState(current ?? '');
  const [inputError, setInputError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (e164: string | null) => resellers.setRecordingDialInE164(resellerId, e164),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller', resellerId] });
      setInputError(null);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('e164_already_in_use') || msg.includes('409')) {
        setInputError('This number is already assigned to another reseller.');
      } else {
        setInputError('Failed to update dial-in number.');
      }
    },
  });

  function handleSave() {
    setInputError(null);
    const val = inputValue.trim();
    if (!val) {
      save.mutate(null);
      return;
    }
    if (!E164_RE.test(val)) {
      setInputError('Enter a valid E.164 number, e.g. +12125550100');
      return;
    }
    save.mutate(val);
  }

  function handleClear() {
    setInputValue('');
    setInputError(null);
    save.mutate(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recording dial-in line</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Admins can call this E.164 number to record directly to any folder in
          this reseller&rsquo;s library. Authentication is by caller ID — the
          caller&rsquo;s number must appear in a portal user&rsquo;s verified
          numbers. Leave blank to disable.
        </p>

        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor={`dial-in-${resellerId}`} className="sr-only">
              Recording dial-in line
            </Label>
            <PhoneInput
              id={`dial-in-${resellerId}`}
              value={inputValue}
              onChange={(v) => {
                setInputValue(v);
                setInputError(null);
              }}
              resellerId={resellerId}
              placeholder="555 000 0000"
              aria-invalid={!!inputError}
              aria-describedby={inputError ? `dial-in-err-${resellerId}` : undefined}
            />
            {inputError && (
              <p
                id={`dial-in-err-${resellerId}`}
                role="alert"
                className="text-xs text-destructive"
              >
                {inputError}
              </p>
            )}
          </div>

          <Button
            size="sm"
            disabled={save.isPending}
            onClick={handleSave}
            className="shrink-0"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>

          {current && (
            <Button
              size="sm"
              variant="outline"
              disabled={save.isPending}
              onClick={handleClear}
              className="shrink-0"
            >
              {save.isPending ? 'Clearing…' : 'Clear'}
            </Button>
          )}
        </div>

        {current && !save.isPending && (
          <p className="text-xs text-muted-foreground">
            Current: <code className="font-mono">{current}</code>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────
// Recycle-bin config card — per-reseller retention max + per-30-day unit
// price (used for billing display). Migration 0059.
// ──────────────────────────────────────────────────────────

export function RecycleBinConfigCard({ reseller }: { reseller: ResellerRow }) {
  const qc = useQueryClient();
  const [maxDays, setMaxDays] = useState<number>(
    reseller.recording_recycle_bin_retention_days_max ?? 90,
  );
  const [unitPriceDollars, setUnitPriceDollars] = useState<string>(
    String(((reseller.recording_recycle_bin_unit_price_cents ?? 0) / 100).toFixed(2)),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const cents = Math.round(Number(unitPriceDollars) * 100);
      return resellers.update(reseller.id, {
        recordingRecycleBinRetentionDaysMax: maxDays,
        recordingRecycleBinUnitPriceCents: Number.isFinite(cents) && cents >= 0 ? cents : 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller', reseller.id] });
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
  });

  const dirty =
    maxDays !== (reseller.recording_recycle_bin_retention_days_max ?? 90) ||
    Math.round(Number(unitPriceDollars) * 100) !==
      (reseller.recording_recycle_bin_unit_price_cents ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recordings recycle bin</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">
          Each customer chooses a recycle-bin retention up to your cap.
          Retention is billed per 30-day unit per customer per month
          (30 days = 1×, 60 = 2×, 90 = 3× the unit price).
        </p>

        <div className="space-y-2">
          <Label htmlFor={`bin-max-${reseller.id}`}>Maximum retention (days)</Label>
          <div className="flex items-center gap-3">
            <input
              id={`bin-max-${reseller.id}`}
              type="range"
              min={1}
              max={90}
              step={1}
              value={maxDays}
              onChange={(e) => setMaxDays(Number(e.target.value))}
              className="flex-1"
            />
            <div className="w-20 text-right font-mono tabular-nums">{maxDays} d</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Customers cannot choose a longer window than this. Platform cap is 90 days.
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`bin-price-${reseller.id}`}>
            Price per 30-day unit ($/customer/month)
          </Label>
          <Input
            id={`bin-price-${reseller.id}`}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={unitPriceDollars}
            onChange={(e) => setUnitPriceDollars(e.target.value)}
            className="font-mono w-32"
          />
          <p className="text-xs text-muted-foreground">
            Set to 0 to absorb the cost. Billing line items are out of scope of
            this release; this value is stored for future invoicing wiring.
          </p>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {error && (
            <span role="alert" className="text-xs text-destructive">{error}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Super-admin toggle: may this reseller use the platform's shared email
 * sender? When OFF, their portal hides the "Use platform email" option
 * and the server rejects any attempt to set mode=platform.
 */
function PlatformEmailToggle({ resellerId }: { resellerId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['reseller-email-settings', resellerId],
    queryFn: () => resellerEmailSettings.get(resellerId),
  });
  const toggle = useMutation({
    mutationFn: (allowed: boolean) =>
      resellerEmailSettings.setPlatformAllowed(resellerId, allowed),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller-email-settings', resellerId] });
    },
  });

  if (isLoading || !data) return null;
  const allowed = data.per_reseller_platform_allowed;

  return (
    <div className="space-y-2">
      <p className="font-medium">{t('reseller.platformEmailAccess')}</p>
      <p className="text-xs text-muted-foreground">
        {t('reseller.platformEmailAccessDescription')}
      </p>
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={allowed}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
          className="accent-primary h-4 w-4"
        />
        <span>
          {allowed
            ? t('reseller.platformEmailAllowed')
            : t('reseller.platformEmailBlocked')}
        </span>
      </label>
      {toggle.isError && (
        <p role="alert" className="text-xs text-destructive">
          {toggle.error instanceof Error ? toggle.error.message : t('common.failedToSave')}
        </p>
      )}
    </div>
  );
}
